import { Alert, Snackbar } from '@mui/material';
import { createContext, useCallback, useContext, useMemo, useState, type JSX, type ReactNode } from 'react';

type Severity = 'success' | 'error' | 'info';

interface Notification {
  readonly id: number;
  readonly message: string;
  readonly severity: Severity;
}

type Notify = (message: string, severity?: Severity) => void;

const NotifyContext = createContext<Notify | null>(null);

export function useNotify(): Notify {
  const ctx = useContext(NotifyContext);
  if (!ctx) {
    throw new Error('useNotify: ausserhalb von NotificationProvider verwendet.');
  }
  return ctx;
}

let nextId = 0;

/**
 * business-logic-model-web-oberflaeche, Abschnitt "Benachrichtigungen
 * (Snackbar)": jede erfolgreiche schreibende Aktion löst Snackbar + Alert
 * aus, Dauer 4s, schließbar (F-19).
 */
export function NotificationProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [queue, setQueue] = useState<readonly Notification[]>([]);

  const notify = useCallback<Notify>((message, severity = 'success') => {
    setQueue((prev) => [...prev, { id: nextId++, message, severity }]);
  }, []);

  const handleClose = useCallback((id: number) => {
    setQueue((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const current = queue[0];

  const contextValue = useMemo(() => notify, [notify]);

  return (
    <NotifyContext.Provider value={contextValue}>
      {children}
      {current && (
        <Snackbar
          data-testid="notification-snackbar"
          open
          autoHideDuration={4000}
          onClose={() => handleClose(current.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert severity={current.severity} onClose={() => handleClose(current.id)}>
            {current.message}
          </Alert>
        </Snackbar>
      )}
    </NotifyContext.Provider>
  );
}
